import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Calendar,
  Clock,
  MapPin,
  Building2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Ban,
  CreditCard,
  Heart,
  ChevronRight,
} from 'lucide-react'
import { DashboardLayout } from '../components/DashboardLayout'
import { BookingDetailModal } from '../components/BookingDetailModal'
import { FilterTabs } from '../components/FilterTabs'
import { RecurringSeriesPanel } from '../components/RecurringSeriesPanel'
import { subscribeToSSE } from '../components/NotificationBell'
import {
  UnifiedBookingModal,
  type BookingSubmitData,
} from '../components/UnifiedBookingModal'
import { useAuthStore } from '../lib/auth'
import { groupConsecutiveSlots, blockToTimeRange } from '../lib/utils'
import { api, type MyBooking, type Room, type RoomSchedule } from '../lib/api'

// A booking handed over from an accepted access-request invite: the guest asked for
// a specific room/date/time, the admin approved them, and we must not lose that
// intent when they arrive here after accepting.
interface PendingInviteBooking {
  roomId: string
  preferredDate: string
  preferredTime: string | null
  preferredSlots: string | null
  message: string | null
  orgId: string
}

const statusConfig: Record<
  string,
  { label: string; color: string; bg: string; icon: typeof CheckCircle }
> = {
  pending: {
    label: 'Waiting for approval',
    color: 'text-yellow-700',
    bg: 'bg-yellow-100',
    icon: AlertCircle,
  },
  confirmed: { label: 'Confirmed', color: 'text-green-700', bg: 'bg-green-100', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'text-gray-700', bg: 'bg-gray-100', icon: XCircle },
  denied: { label: 'Declined', color: 'text-red-700', bg: 'bg-red-100', icon: Ban },
  completed: { label: 'Completed', color: 'text-blue-700', bg: 'bg-blue-100', icon: CheckCircle },
}

type Filter = 'upcoming' | 'pending' | 'payment_due' | 'past' | 'cancelled' | 'all' | 'recurring'

// The personal bookings page: every booking the signed-in user has made, across all
// organisations, in one simple list. Booking new rooms happens in the marketplace.
export function MyBookingsPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading, checkAuth, memberships, currentOrg, setCurrentOrg } =
    useAuthStore()
  const queryClient = useQueryClient()

  const [filter, setFilter] = useState<Filter>('upcoming')
  const [selectedBooking, setSelectedBooking] = useState<MyBooking | null>(null)
  const [pendingOpenId, setPendingOpenId] = useState<string | null>(null)

  // Accepted-invite booking flow state
  const [pendingInvite, setPendingInvite] = useState<PendingInviteBooking | null>(null)
  const [inviteRoom, setInviteRoom] = useState<Room | null>(null)
  const [inviteSchedules, setInviteSchedules] = useState<RoomSchedule[]>([])
  const [showInviteBookingModal, setShowInviteBookingModal] = useState(false)

  // Pick up a booking intent left by the accept-invite page and load what the
  // booking form needs (room + its schedules, falling back to building hours)
  useEffect(() => {
    if (!isAuthenticated) return
    const pendingJson = localStorage.getItem('pendingBooking')
    if (!pendingJson) return

    let pending: PendingInviteBooking
    try {
      pending = JSON.parse(pendingJson)
    } catch {
      localStorage.removeItem('pendingBooking')
      return
    }
    if (!pending?.roomId) {
      localStorage.removeItem('pendingBooking')
      return
    }

    const load = async () => {
      try {
        // Room lookups are org-scoped, so make sure the invite's org is selected
        const inviteMembership = memberships.find((m) => m.orgId === pending.orgId)
        if (inviteMembership && currentOrg?.orgId !== pending.orgId) {
          await setCurrentOrg(inviteMembership.orgSlug)
        }

        const { room } = await api.rooms.get(pending.roomId)
        const roomScheduleData = await api.rooms.getSchedule(pending.roomId)
        let schedules: RoomSchedule[] = roomScheduleData.schedules
        if (schedules.length === 0 && room.buildingId) {
          const buildingScheduleData = await api.buildings.getOpeningSchedules(room.buildingId)
          schedules = buildingScheduleData.schedules.map((s) => ({
            id: `building-${s.id}`,
            roomId: pending.roomId,
            dayOfWeek: s.dayOfWeek,
            openTime: s.openTime,
            closeTime: s.closeTime,
            isActive: s.isActive,
          }))
        }

        setInviteRoom(room)
        setInviteSchedules(schedules)
        setPendingInvite(pending)
        localStorage.removeItem('pendingBooking')
      } catch (err) {
        console.error('Failed to load pending booking details:', err)
        localStorage.removeItem('pendingBooking')
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  // Turn the request's preferred time into pre-selected half-hour slots
  const inviteSlots = useMemo(() => {
    if (!pendingInvite) return []
    if (pendingInvite.preferredSlots) {
      return pendingInvite.preferredSlots
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    }
    if (pendingInvite.preferredTime) {
      const timeMatch = pendingInvite.preferredTime.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/gi)
      if (timeMatch) {
        return timeMatch
          .map((str) => {
            const match = str.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i)
            if (!match || !match[1]) return null
            let hours = parseInt(match[1])
            const minutes = match[2] ? parseInt(match[2]) : 0
            const period = match[3]?.toUpperCase()
            if (period === 'PM' && hours !== 12) hours += 12
            if (period === 'AM' && hours === 12) hours = 0
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
          })
          .filter((s): s is string => s !== null)
      }
    }
    return []
  }, [pendingInvite])

  useEffect(() => {
    checkAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login', search: { returnTo: window.location.pathname + window.location.search } })
    }
  }, [authLoading, isAuthenticated, navigate])

  // Handle returns from Stripe Checkout (payment or donation) and deep links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const bookingId = params.get('booking')

    if (params.get('payment') === 'success' && bookingId) {
      api.stripe
        .verifyPayment(bookingId)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
          window.history.replaceState({}, '', '/my-bookings')
        })
        .catch((err) => console.error('Failed to verify payment:', err))
    }

    const donationStatus = params.get('donation')
    if (donationStatus === 'success' && bookingId) {
      api.stripe
        .verifyDonation(bookingId)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
          window.history.replaceState({}, '', '/my-bookings')
        })
        .catch((err) => console.error('Failed to verify donation:', err))
    } else if (donationStatus === 'cancelled' || params.get('payment') === 'cancelled') {
      window.history.replaceState({}, '', '/my-bookings')
    }

    const openId = params.get('open')
    if (openId) {
      setPendingOpenId(openId)
      window.history.replaceState({}, '', '/my-bookings')
    }

    const urlFilter = params.get('filter')
    if (
      urlFilter === 'pending' ||
      urlFilter === 'past' ||
      urlFilter === 'payment_due' ||
      urlFilter === 'cancelled' ||
      urlFilter === 'all'
    ) {
      setFilter(urlFilter)
    }
  }, [queryClient])

  const { data, isLoading } = useQuery({
    queryKey: ['my-bookings'],
    queryFn: () => api.bookings.mine(),
    enabled: isAuthenticated,
  })

  const bookings = useMemo(() => data?.bookings || [], [data])

  // Refresh in real time when a booking changes (approval, payment, donation)
  useEffect(() => {
    const unsubNotification = subscribeToSSE('notification', () => {
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
    })
    const unsubBookingUpdate = subscribeToSSE('booking_update', () => {
      queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
    })
    return () => {
      unsubNotification()
      unsubBookingUpdate()
    }
  }, [queryClient])

  // Open a booking from a deep link once the list has loaded
  useEffect(() => {
    if (pendingOpenId && bookings.length > 0) {
      const booking = bookings.find((b) => b.id === pendingOpenId)
      if (booking) {
        setSelectedBooking(booking)
        setPendingOpenId(null)
      }
    }
  }, [pendingOpenId, bookings])

  const now = useMemo(() => new Date(), [])

  const grouped = useMemo(() => {
    const upcoming = bookings
      .filter(
        (b) =>
          new Date(b.endTime) >= now && (b.status === 'confirmed' || b.status === 'pending')
      )
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
    const pending = bookings.filter((b) => b.status === 'pending')
    const paymentDue = bookings.filter(
      (b) =>
        b.status === 'confirmed' && b.paymentStatus === 'pending' && Number(b.totalPrice) > 0
    )
    const past = bookings.filter(
      (b) =>
        new Date(b.endTime) < now && (b.status === 'confirmed' || b.status === 'completed')
    )
    const cancelled = bookings.filter((b) => b.status === 'cancelled' || b.status === 'denied')
    return { upcoming, pending, paymentDue, past, cancelled }
  }, [bookings, now])

  const filtered =
    filter === 'upcoming'
      ? grouped.upcoming
      : filter === 'pending'
        ? grouped.pending
        : filter === 'payment_due'
          ? grouped.paymentDue
          : filter === 'past'
            ? grouped.past
            : filter === 'cancelled'
              ? grouped.cancelled
              : bookings

  const filterOptions = useMemo(
    () => [
      { value: 'upcoming', label: 'Upcoming' },
      {
        value: 'pending',
        label: 'Waiting',
        count: grouped.pending.length,
        countColor: 'amber' as const,
      },
      {
        value: 'payment_due',
        label: 'Payment',
        count: grouped.paymentDue.length,
        countColor: 'red' as const,
      },
      { value: 'past', label: 'Past', count: grouped.past.length, countColor: 'gray' as const },
      {
        value: 'cancelled',
        label: 'Cancelled',
        count: grouped.cancelled.length,
        countColor: 'gray' as const,
      },
      { value: 'all', label: 'All' },
      { value: 'recurring', label: 'Recurring' },
    ],
    [grouped]
  )

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) return null

  return (
    <DashboardLayout title="My Bookings" subtitle="All your bookings in one place">
      {/* Primary action: book a room via the marketplace */}
      <div className="mb-6">
        <Link
          to="/marketplace"
          className="inline-flex items-center gap-2 px-5 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          <Building2 className="w-5 h-5" />
          Book a Room
        </Link>
      </div>

      <FilterTabs
        options={filterOptions}
        value={filter}
        onChange={(value) => setFilter(value as Filter)}
      />

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      )}

      {filter === 'recurring' && <RecurringSeriesPanel personal />}

      {!isLoading && filter !== 'recurring' && filtered.length === 0 && (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {filter === 'pending'
              ? 'Nothing waiting for approval'
              : filter === 'payment_due'
                ? 'No payments due'
                : filter === 'past'
                  ? 'No past bookings yet'
                  : filter === 'cancelled'
                    ? 'No cancelled bookings'
                    : 'No bookings yet'}
          </h3>
          {(filter === 'upcoming' || filter === 'all') && (
            <>
              <p className="text-gray-500 mb-4">
                When you book a room, it will appear here.
              </p>
              <Link
                to="/marketplace"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Building2 className="w-4 h-4" />
                Book a Room
              </Link>
            </>
          )}
        </div>
      )}

      {!isLoading && filter !== 'recurring' && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((booking) => {
            const startTime = new Date(booking.startTime)
            const endTime = new Date(booking.endTime)
            const statusInfo = statusConfig[booking.status]
            const StatusIcon = statusInfo?.icon ?? AlertCircle
            const paymentDue =
              booking.status === 'confirmed' &&
              booking.paymentStatus === 'pending' &&
              Number(booking.totalPrice) > 0
            const donationPledged =
              booking.isVoluntaryDonation &&
              Number(booking.donationAmount || 0) > 0 &&
              !booking.donationPaidAt

            return (
              <button
                key={booking.id}
                onClick={() => setSelectedBooking(booking)}
                className="w-full text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <h3 className="font-medium text-gray-900 truncate">
                        {booking.roomName || booking.title || 'Room booking'}
                      </h3>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusInfo?.bg ?? 'bg-gray-100'} ${statusInfo?.color ?? 'text-gray-700'}`}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {statusInfo?.label ?? booking.status}
                      </span>
                      {paymentDue && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-orange-100 text-orange-700">
                          <CreditCard className="w-3 h-3" />
                          Payment needed
                        </span>
                      )}
                      {donationPledged && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-rose-100 text-rose-700">
                          <Heart className="w-3 h-3" />
                          Donation pledged
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {startTime.toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          year:
                            startTime.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
                        })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {startTime.toLocaleTimeString('en-GB', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                        -{' '}
                        {endTime.toLocaleTimeString('en-GB', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <span className="flex items-center gap-1 truncate">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">
                          {[booking.buildingName, booking.orgName].filter(Boolean).join(', ') ||
                            'Location'}
                        </span>
                      </span>
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </button>
            )
          })}
        </div>
      )}

      {selectedBooking && (
        <BookingDetailModal
          booking={selectedBooking}
          orgSlug={selectedBooking.orgSlug || undefined}
          roomName={selectedBooking.roomName || undefined}
          buildingName={selectedBooking.buildingName || undefined}
          onClose={() => setSelectedBooking(null)}
          onUpdate={() => {
            queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
            setSelectedBooking(null)
          }}
        />
      )}

      {/* Accepted-invite prompt: finish the booking they originally requested */}
      {pendingInvite && inviteRoom && !showInviteBookingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="bg-primary-50 px-6 py-4 rounded-t-xl border-b border-primary-100">
              <h2 className="text-lg font-semibold text-primary-900">Complete Your Booking</h2>
              <p className="text-sm text-primary-700 mt-1">
                Your access request was approved. Let&apos;s finish booking your room.
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Room:</span>
                  <span className="font-medium text-gray-900">{inviteRoom.name}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">Requested Date:</span>
                  <span className="text-gray-900">
                    {new Date(pendingInvite.preferredDate + 'T12:00:00').toLocaleDateString(
                      'en-GB',
                      { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
                    )}
                  </span>
                </div>
                {pendingInvite.preferredTime && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600 ml-6">Requested Time:</span>
                    <span className="text-gray-900">{pendingInvite.preferredTime}</span>
                  </div>
                )}
                {pendingInvite.message && (
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <p className="text-sm text-gray-600 italic">
                      &ldquo;{pendingInvite.message}&rdquo;
                    </p>
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-600">
                Click below to open the booking form with your requested details pre-filled.
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex gap-3 justify-end">
              <button
                onClick={() => setPendingInvite(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={() => setShowInviteBookingModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Complete Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Booking form for the accepted invite, pre-filled with the requested details */}
      {pendingInvite && inviteRoom && showInviteBookingModal && (
        <UnifiedBookingModal
          room={inviteRoom}
          roomName={inviteRoom.name}
          schedules={inviteSchedules}
          currency="GBP"
          canBookOutOfHours={
            memberships.find((m) => m.orgId === pendingInvite.orgId)?.canBookOutOfHours || false
          }
          mode="member"
          orgSlug={memberships.find((m) => m.orgId === pendingInvite.orgId)?.orgSlug}
          initialDate={pendingInvite.preferredDate}
          initialSlots={inviteSlots.length > 0 ? inviteSlots : undefined}
          initialNotes={pendingInvite.message || ''}
          onClose={() => {
            setShowInviteBookingModal(false)
            setPendingInvite(null)
            queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
          }}
          onSubmit={async (data: BookingSubmitData) => {
            const dateObj = new Date(data.date + 'T00:00:00')
            const blocks =
              data.selectedSlots && data.selectedSlots.length > 0
                ? groupConsecutiveSlots(data.selectedSlots)
                : [[data.startTime]]

            let firstBooking: { id: string; status: string } | null = null

            for (const block of blocks) {
              const { start, end } = blockToTimeRange(block)
              const startTime = new Date(dateObj)
              const [sh = 0, sm = 0] = start.split(':').map(Number)
              startTime.setHours(sh, sm, 0, 0)
              const endTime = new Date(dateObj)
              const [eh = 0, em = 0] = end.split(':').map(Number)
              endTime.setHours(eh, em, 0, 0)

              const result = await api.bookings.create({
                roomId: data.roomId,
                startTime: startTime.toISOString(),
                endTime: endTime.toISOString(),
                localStartTime: start,
                localEndTime: end,
                title: data.title,
                notes: data.notes,
                donationAmount: firstBooking ? undefined : data.donationAmount,
              })
              if (!firstBooking) firstBooking = result.booking
            }

            queryClient.invalidateQueries({ queryKey: ['my-bookings'] })
            return firstBooking
              ? { booking: { id: firstBooking.id, status: firstBooking.status } }
              : undefined
          }}
          fetchAvailability={async (roomId, start, end) => {
            try {
              const result = await api.bookings.list({ start, end, roomId })
              return result.bookings
                .filter((b) => b.status !== 'cancelled' && b.status !== 'denied')
                .map((b) => ({ startTime: b.startTime, endTime: b.endTime, status: b.status }))
            } catch {
              return []
            }
          }}
          submitButtonText="Request Booking"
          title="Complete Your Booking"
        />
      )}
    </DashboardLayout>
  )
}
